import { useMemo } from 'react';

/**
 * Get all permissions for the current user (from roles + direct permissions)
 */
export const getUserPermissions = (): string[] => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return [];
    const user = JSON.parse(userStr);

    const permissions = new Set<string>();

    // Direct permissions
    if (user.permissions && Array.isArray(user.permissions)) {
      user.permissions.forEach((p: any) => {
        permissions.add(typeof p === 'string' ? p : p.name);
      });
    }

    // Permissions inherited from roles
    if (user.roles && Array.isArray(user.roles)) {
      user.roles.forEach((role: any) => {
        if (role.permissions && Array.isArray(role.permissions)) {
          role.permissions.forEach((p: any) => {
            permissions.add(typeof p === 'string' ? p : p.name);
          });
        }
      });
    }

    // Super admin gets all permissions
    if (user.user_type === 'superadmin' || user.user_type === 'admin') {
      return ['*'];
    }

    // County user with Admin role gets all permissions
    if (user.user_type === 'county') {
      const hasAdminRole = user.roles?.some((r: any) => {
        const name = (r.name || r || '').toString().toLowerCase();
        return name.includes('admin');
      });
      if (hasAdminRole) return ['*'];
    }

    return Array.from(permissions);
  } catch {
    return [];
  }
};

/**
 * Check if user has a specific permission
 */
export const checkPermission = (permission: string): boolean => {
  const permissions = getUserPermissions();
  if (permissions.includes('*')) return true;
  return permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 */
export const checkAnyPermission = (...perms: string[]): boolean => {
  const permissions = getUserPermissions();
  if (permissions.includes('*')) return true;
  return perms.some(p => permissions.includes(p));
};

/**
 * React hook for permission checking
 */
export const usePermission = () => {
  const permissions = useMemo(() => getUserPermissions(), []);

  // Also check if current user is an admin directly (belt and suspenders)
  const isAdmin = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      if (user.user_type === 'superadmin' || user.user_type === 'admin') return true;
      if (user.user_type === 'county' && user.roles?.some((r: any) => (r.name || '').toLowerCase().includes('admin'))) return true;
      return false;
    } catch { return false; }
  }, []);

  const can = (permission: string): boolean => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  };

  const canAny = (...perms: string[]): boolean => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return perms.some(p => permissions.includes(p));
  };

  const canAll = (...perms: string[]): boolean => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return perms.every(p => permissions.includes(p));
  };

  return { can, canAny, canAll, permissions, isAdmin };
};
